import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { semitones } from "@shared/prosodyCore.js";
import { MicButton } from "../components/mic/MicButton.js";
import { ConfirmDialog } from "../components/ui/ConfirmDialog.js";
import { MicPorownanie } from "../components/mic/MicPorownanie.js";
import { MicHistory } from "../components/mic/MicHistory.js";
import { MicWynik } from "../components/mic/MicWynik.js";
import { Banner } from "../components/ui/Banner.js";
import { Card } from "../components/ui/Card.js";
import { useMicRecorder } from "../hooks/useMicRecorder.js";
import { api, ApiError, type MicHistoryEntry, type MicShotResponse, type PorownanieDto } from "../lib/api.js";

/**
 * Limit uzywany, ZANIM dojdzie odpowiedz z ustawien. Nie jest zrodlem prawdy -
 * rozstrzyga `micMaxSeconds` z panelu, ktore serwer egzekwuje w micApi.
 *
 * Bez odczytu z ustawien tryb mikrofonu psul sie po cichu: administrator ustawial
 * limit na 3 s, klient dalej nagrywal 15 s, a serwer odbijal KAZDE nagranie kodem
 * 413. Uzytkownik widzial tylko "Nie udalo sie przeanalizowac nagrania".
 */
const DOMYSLNY_LIMIT_S = 15;

/** Co da sie policzyc lokalnie zanim serwer odpowie. */
export interface LocalEstimate {
  f0MedianHz: number | null;
  f0RangeSemitones: number | null;
}

function summarizeTrack(track: Array<number | null>): LocalEstimate {
  const values = track.filter((v): v is number => v !== null).sort((a, b) => a - b);
  if (values.length < 5) return { f0MedianHz: null, f0RangeSemitones: null };

  const at = (p: number) => values[Math.min(values.length - 1, Math.floor((values.length - 1) * p))];
  const p10 = at(0.1);
  const p90 = at(0.9);

  return {
    f0MedianHz: at(0.5),
    f0RangeSemitones: p10 > 0 ? semitones(p10, p90) : null,
  };
}

export function MicPage(): React.JSX.Element {
  const [limitSekund, setLimitSekund] = useState(DOMYSLNY_LIMIT_S);
  /**
   * Okno modelu wymiarow, gdy jest KROTSZE niz limit nagrywania.
   *
   * null znaczy "nie ma o czym mowic": warstwa wylaczona, sidecar nie podal
   * liczby albo model slucha tak dlugo jak mikrofon nagrywa. Zdanie o limicie
   * ma sie pojawiac wylacznie tam, gdzie faktycznie cos obcina.
   */
  const [oknoWymiarowS, setOknoWymiarowS] = useState<number | null>(null);
  const { state, live, elapsedMs, pitchTrack, start, stop, reset } = useMicRecorder(limitSekund);
  const [shot, setShot] = useState<MicShotResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<MicHistoryEntry[]>([]);
  const [historiaNiedostepna, setHistoriaNiedostepna] = useState(false);
  /**
   * Kiedy ruszyla analiza, albo null gdy nie trwa.
   *
   * Potrzebne do licznika w karcie "Nagranie": bez niego stan przetwarzania byl
   * niemym oczekiwaniem, a przy dostawcy odpowiadajacym bledem trwal dziewiec
   * sekund i konczyl sie niczym. Uzytkownik nie mial jak odroznic pracy od zwisu.
   */
  const [analizaOd, setAnalizaOd] = useState<number | null>(null);
  const [analizaMs, setAnalizaMs] = useState(0);
  const przerwijRef = useRef<AbortController | null>(null);

  /**
   * Porownanie dostawcow na tym samym nagraniu.
   *
   * Wynik zyje w stanie strony, nie w bazie: trwalym zapisem sa wiersze w
   * dzienniku wywolan ze wspolnym operationId, ktore i tak wchodza do paczki
   * audytu. Trzymanie tego drugi raz dawaloby dwa zrodla prawdy o jednej
   * operacji.
   */
  const [porownanie, setPorownanie] = useState<PorownanieDto | null>(null);
  const [porownanieTrwa, setPorownanieTrwa] = useState(false);
  const [porownanieBlad, setPorownanieBlad] = useState<string | null>(null);
  const [pytamOPorownanie, setPytamOPorownanie] = useState(false);
  const [liczbaDostawcow, setLiczbaDostawcow] = useState<number | null>(null);

  const localEstimate = useMemo(() => summarizeTrack(pitchTrack), [pitchTrack]);

  /**
   * Pyta o zgode, podajac LICZBE WYWOLAN. Operacja jest platna, wiec
   * "czy na pewno" bez liczby nie jest pytaniem, tylko formalnoscia.
   */
  async function zapytajOPorownanie(): Promise<void> {
    setPorownanieBlad(null);
    try {
      const { dostawcy } = await api.micDostawcyPorownania();
      setLiczbaDostawcow(dostawcy.length);
    } catch {
      // Nie znamy liczby, ale to nie powod, zeby zablokowac funkcje - dialog
      // powie wtedy ogolnie, ile to kosztuje.
      setLiczbaDostawcow(null);
    }
    setPytamOPorownanie(true);
  }

  async function porownaj(): Promise<void> {
    setPytamOPorownanie(false);
    if (!shot?.id) return;
    setPorownanieTrwa(true);
    setPorownanieBlad(null);
    try {
      setPorownanie(await api.micPorownaj(shot.id));
    } catch (err) {
      const kod = err instanceof Error ? err.message : "";
      setPorownanieBlad(
        kod.includes("brak_nagrania")
          ? "Tej próby nie da się porównać, bo jej nagranie nie zostało zapisane albo minęło już 30 dni. Nagraj nowe zdanie."
          : "Nie udało się przepuścić nagrania przez dostawców. Sprawdź klucze w Ustawieniach i spróbuj ponownie.",
      );
    } finally {
      setPorownanieTrwa(false);
    }
  }

  /*
   * Poprzednia proba, czyli punkt odniesienia dla trendu w kafelkach (KP-1).
   * Biezacy strzal jest juz w historii, gdy zostal zapisany, wiec trzeba go
   * odfiltrowac - inaczej "poprzednia" byla by ta sama proba i trend zawsze
   * wynosilby zero.
   */
  const poprzednia = useMemo(() => {
    const bezBiezacej = shot?.id ? history.filter((h) => h.id !== shot.id) : history;
    return bezBiezacej[0] ?? null;
  }, [history, shot]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.micHistory(5);
      setHistory(res.shots);
      // Serwer oddaje pusta liste z flaga, zamiast bledu - ale flaga musi dojsc
      // na ekran, inaczej niedostepna baza wyglada jak brak wczesniejszych prob.
      setHistoriaNiedostepna(res.unavailable === true);
    } catch {
      // Historia to wygoda. Jej brak nie moze psuc glownego widoku - ale tez nie
      // ma udawac, ze uzytkownik po prostu jeszcze nic nie nagral.
      setHistoriaNiedostepna(true);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  // Licznik czasu analizy. Sekundowy krok wystarczy - to ma pokazac, ze cos sie
  // dzieje, a nie mierzyc opoznienie z dokladnoscia do milisekundy.
  useEffect(() => {
    if (analizaOd === null) {
      setAnalizaMs(0);
      return;
    }
    setAnalizaMs(0);
    const id = setInterval(() => setAnalizaMs(Date.now() - analizaOd), 250);
    return () => clearInterval(id);
  }, [analizaOd]);

  // Limit nagrania jest ustawieniem panelu, wiec klient musi go znac - inaczej
  // nagrywa dluzej, niz serwer przyjmie, i kazda proba konczy sie odbiciem.
  useEffect(() => {
    void (async () => {
      try {
        const dane = await api.settings();
        setLimitSekund(dane.settings.micMaxSeconds);

        // Ktory model liczy wymiary - wybrany w panelu albo domyslny dostawcy.
        const warstwa = dane.providers?.ser.find((p) => p.id === dane.settings.serProvider);
        const wybrany = dane.settings.serModel ?? warstwa?.model ?? null;
        const okno = warstwa?.modelInfo?.find((m) => m.id === wybrany)?.maxSeconds ?? null;
        setOknoWymiarowS(okno !== null && okno < dane.settings.micMaxSeconds ? okno : null);
      } catch {
        // Brak ustawien nie moze zablokowac nagrywania - zostaje wartosc domyslna.
      }
    })();
  }, []);

  /**
   * Komunikat nazywajacy PRZYCZYNE (S-1, S-2).
   *
   * "Spróbuj jeszcze raz" przy wygasłej sesji kazalo uzytkownikowi powtarzac
   * czynnosc, ktora nie ma prawa sie udac, dopoki sie nie zaloguje - a przy
   * odbitym limicie powtarzac nagranie tej samej dlugosci.
   */
  function komunikatBledu(err: unknown): string {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        return "Sesja wygasła. Zaloguj się ponownie - nagranie nie zostało przeanalizowane.";
      }
      if (err.code === "nagranie_za_dlugie") {
        return `Nagranie jest dłuższe niż ustawiony limit ${limitSekund} s. Zmień limit w Ustawieniach albo powiedz krócej.`;
      }
      if (err.code === "nieobslugiwany_format") {
        return "Ta przeglądarka nagrała dźwięk w formacie, którego serwer nie przyjmuje.";
      }
      if (err.status === 504 || err.status === 502) {
        return "Serwer nie odpowiedział na czas. Spróbuj jeszcze raz - jeśli powtórzy się, sprawdź dostawców w Ustawieniach.";
      }
    }
    return "Nie udało się przeanalizować nagrania. Spróbuj jeszcze raz.";
  }

  async function handleRecord(): Promise<void> {
    setShot(null);
    setError(null);

    const recorded = await start();
    if (!recorded) return;

    const controller = new AbortController();
    przerwijRef.current = controller;
    setAnalizaOd(Date.now());

    try {
      const result = await api.micAnalyze(
        recorded.blob,
        recorded.mimeType,
        recorded.durationMs,
        controller.signal,
      );
      setShot(result);
      void loadHistory();
    } catch (err) {
      // Przerwanie jest decyzja uzytkownika, nie awaria - nie ma o czym krzyczec.
      if (!(err instanceof DOMException && err.name === "AbortError")) {
        setError(komunikatBledu(err));
      }
    } finally {
      przerwijRef.current = null;
      setAnalizaOd(null);
      reset();
    }
  }

  return (
    <>
      <h1 className="page-title">Mikrofon</h1>
      <p className="page-lead">
        Powiedz jedno zdanie i zobacz, jak zabrzmiało. Tempo i ton mierzymy z sygnału, treść i emocję
        rozpoznaje model.
      </p>

      {/* BN-2: baner stanu pod naglowkiem strony i nad kartami; BN-3: jeden naraz. */}
      {error && <Banner wariant="danger" tekst={error} />}

      <Card tytul="Nagranie">
        <MicButton
          state={state}
          level={live.level}
          f0Hz={live.f0Hz}
          elapsedMs={elapsedMs}
          maxSeconds={limitSekund}
          oknoWymiarowS={oknoWymiarowS}
          pitchTrack={pitchTrack}
          onStart={() => void handleRecord()}
          onStop={stop}
          analizaMs={analizaMs}
          onPrzerwij={() => przerwijRef.current?.abort()}
        />
      </Card>

      <MicWynik
        shot={shot}
        localEstimate={localEstimate}
        pending={state === "processing"}
        poprzednia={poprzednia}
        naPorownaj={shot?.id && shot.audioZapisane ? zapytajOPorownanie : undefined}
        porownanieTrwa={porownanieTrwa}
      />

      {(porownanie || porownanieTrwa || porownanieBlad) && (
        <MicPorownanie wynik={porownanie} trwa={porownanieTrwa} blad={porownanieBlad} />
      )}

      <MicHistory entries={history} niedostepna={historiaNiedostepna} />

      <ConfirmDialog
        otwarty={pytamOPorownanie}
        tytul="Puścić to nagranie przez wszystkich dostawców?"
        tresc={
          liczbaDostawcow === null
            ? "Każdy skonfigurowany dostawca dostanie ten sam plik. To są płatne wywołania modeli."
            : `Pójdzie ${liczbaDostawcow} ${liczbaDostawcow === 1 ? "wywołanie" : "wywołania"} do modeli, każde na tym samym pliku. To jest płatne.`
        }
        etykietaPotwierdzenia="Porównaj dostawców"
        onPotwierdz={() => void porownaj()}
        onAnuluj={() => setPytamOPorownanie(false)}
      />
    </>
  );
}
